import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '@/components/shared/DatePicker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import baitStationIcon from '@/assets/equipment/bait-station.svg?inline'
import insectDetectorIcon from '@/assets/equipment/insect-detector.svg?inline'
import mechanicalTrapIcon from '@/assets/equipment/mechanical-trap.svg?inline'
import otherIcon from '@/assets/equipment/other.svg?inline'
import uvLampIcon from '@/assets/equipment/uv-lamp.svg?inline'
import {
  MANUAL_EQUIPMENT_TYPES,
  type ClientAsset,
  type ClientAssetRequest,
  type ManualEquipmentType,
} from '@/types'
import { cn } from '@/lib/utils'

type EquipmentFormState = {
  label: ManualEquipmentType | ''
  serial: string
  location: string
  installation_date: string
  note: string
}

interface AssetFormDialogProps {
  open: boolean
  asset: ClientAsset | null
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (body: ClientAssetRequest) => Promise<void>
}

const emptyForm: EquipmentFormState = {
  label: '',
  serial: '',
  location: '',
  installation_date: '',
  note: '',
}

const equipmentIconUrls: Record<ManualEquipmentType, string> = {
  'Bait Station': baitStationIcon,
  'Insect detector': insectDetectorIcon,
  'Mechanical Trap': mechanicalTrapIcon,
  'UV lamp': uvLampIcon,
  Other: otherIcon,
}

const closeIconUrl = new URL('../../../assets/equipment/x.svg', import.meta.url)
  .href

const equipmentLabels: Record<ManualEquipmentType, string> = {
  'Bait Station': 'Bait station',
  'Insect detector': 'Insect detector',
  'Mechanical Trap': 'Mechanical Trap',
  'UV lamp': 'UV lamp',
  Other: 'Others',
}

const fieldLabelClass =
  'text-xs font-medium leading-4 text-[#171a20] peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
const fieldClass =
  'h-10 rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#6c7285] focus-visible:ring-0 focus-visible:ring-offset-0'
const datePickerClass =
  'rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 shadow-none focus-within:ring-0 [&_button]:text-[#272c36] [&_input]:font-medium [&_input]:text-[#171a20] [&_input]:placeholder:font-normal [&_input]:placeholder:text-[#6c7285]'

function toForm(asset: ClientAsset | null): EquipmentFormState {
  if (!asset) return emptyForm

  return {
    label: MANUAL_EQUIPMENT_TYPES.includes(asset.label as ManualEquipmentType)
      ? (asset.label as ManualEquipmentType)
      : '',
    serial: asset.serial ?? String(asset.meta?.serial ?? ''),
    location: asset.location ?? '',
    installation_date: asset.installation_date?.slice(0, 10) ?? '',
    note: asset.note ?? String(asset.meta?.note ?? ''),
  }
}

function toPayload(form: EquipmentFormState): ClientAssetRequest {
  return {
    label: form.label,
    serial: form.serial.trim() || null,
    location: form.location.trim() || null,
    installation_date: form.installation_date || null,
    note: form.note.trim() || null,
  }
}

export function AssetFormDialog({
  open,
  asset,
  isSaving,
  onOpenChange,
  onSubmit,
}: AssetFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <AssetFormDialogPanel
          key={asset?.id ?? 'new'}
          asset={asset}
          isSaving={isSaving}
          onOpenChange={onOpenChange}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  )
}

function AssetFormDialogPanel({
  asset,
  isSaving,
  onOpenChange,
  onSubmit,
}: Omit<AssetFormDialogProps, 'open'>) {
  const { t } = useTranslation('clients')
  const [form, setForm] = useState<EquipmentFormState>(() => toForm(asset))
  const [submitted, setSubmitted] = useState(false)
  const isEditing = Boolean(asset)

  const title = isEditing
    ? t('assets.panel.editTitle')
    : t('assets.panel.title')

  const typeOptions = useMemo(
    () =>
      MANUAL_EQUIPMENT_TYPES.map(type => ({
        type,
        label: equipmentLabels[type],
        icon: equipmentIconUrls[type],
      })),
    []
  )

  const submit = async () => {
    setSubmitted(true)
    if (!form.label) return
    await onSubmit(toPayload(form))
  }

  return (
    <DialogContent
      side='right'
      className='tf-right-drawer-short gap-0 border-l shadow-[-16px_0_32px_-4px_rgba(75,80,95,0.1),-4px_0_4px_-4px_rgba(75,80,95,0.05)] [&>button]:hidden'
    >
      <DialogHeader className='tf-right-drawer-header tf-right-drawer-header-accent flex-row text-left'>
        <DialogTitle className='min-w-0 flex-1 font-heading text-xl font-semibold leading-[1.2] text-[#171a20] mb-0'>
          {title}
        </DialogTitle>
        <button
          type='button'
          className='inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#f4f5f8] p-2.5'
          onClick={() => onOpenChange(false)}
          disabled={isSaving}
          aria-label={t('assets.actions.cancel')}
        >
          <img alt='' src={closeIconUrl} className='shrink-0' />
        </button>
      </DialogHeader>

      <div className='min-h-0 flex-1 overflow-y-auto bg-[#f4f5f8] p-6'>
        <section className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
          <header className='flex h-[42px] items-center overflow-hidden pr-6 pt-4'>
            <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] px-4'>
              <h3 className='text-sm font-semibold uppercase leading-[1.2] text-[#3d434e]'>
                {t('assets.panel.information')}
              </h3>
            </div>
          </header>

          <div className='flex flex-col gap-4 px-4 pb-4 pt-2'>
            <div className='space-y-1'>
              <div className='flex items-center gap-1'>
                <Label className={fieldLabelClass}>
                  {t('assets.fields.type')}
                </Label>
                <span className='text-xs font-semibold leading-4 text-[#d63b33]'>
                  *
                </span>
              </div>
              <div className='flex flex-wrap gap-2'>
                {typeOptions.map(option => {
                  const active = form.label === option.type
                  return (
                    <button
                      key={option.type}
                      type='button'
                      onClick={() =>
                        setForm(prev => ({ ...prev, label: option.type }))
                      }
                      className={cn(
                        'inline-flex h-[38px] items-center gap-2 rounded-xl border border-[#e7e9ef] bg-white px-4 py-2.5 text-sm font-medium leading-[1.4] text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)]',
                        active && 'border-[#059669] bg-[#ecfdf5]'
                      )}
                    >
                      <span className='inline-flex size-4 shrink-0 items-center justify-center'>
                        <img
                          alt=''
                          aria-hidden
                          src={option.icon}
                          className='max-h-full max-w-full'
                        />
                      </span>
                      <span>{option.label}</span>
                    </button>
                  )
                })}
              </div>
              {submitted && !form.label && (
                <p className='text-sm text-destructive'>
                  {t('assets.validation.required')}
                </p>
              )}
            </div>

            <div className='space-y-1'>
              <Label htmlFor='equipment-serial' className={fieldLabelClass}>
                {t('assets.fields.serial')}
              </Label>
              <Input
                id='equipment-serial'
                value={form.serial}
                onChange={e =>
                  setForm(prev => ({ ...prev, serial: e.target.value }))
                }
                placeholder={t('assets.fields.serialPlaceholder')}
                className={fieldClass}
              />
            </div>

            <div className='space-y-1'>
              <Label htmlFor='equipment-location' className={fieldLabelClass}>
                {t('assets.fields.location')}
              </Label>
              <Input
                id='equipment-location'
                value={form.location}
                onChange={e =>
                  setForm(prev => ({ ...prev, location: e.target.value }))
                }
                placeholder={t('assets.fields.enterHere')}
                className={fieldClass}
              />
            </div>

            <div className='space-y-1'>
              <Label
                htmlFor='equipment-installation-date'
                className={fieldLabelClass}
              >
                {t('assets.fields.installationDate')}
              </Label>
              <DatePicker
                id='equipment-installation-date'
                value={form.installation_date}
                onChange={value =>
                  setForm(prev => ({
                    ...prev,
                    installation_date: value,
                  }))
                }
                variant='field'
                className={datePickerClass}
              />
            </div>

            <div className='space-y-1'>
              <Label htmlFor='equipment-note' className={fieldLabelClass}>
                {t('assets.fields.notes')}
              </Label>
              <Textarea
                id='equipment-note'
                value={form.note}
                onChange={e =>
                  setForm(prev => ({ ...prev, note: e.target.value }))
                }
                placeholder={t('assets.fields.notePlaceholder')}
                className='min-h-[80px] resize-y rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm font-medium leading-[1.4] text-[#171a20] placeholder:text-[#6c7285] focus-visible:ring-0 focus-visible:ring-offset-0'
              />
            </div>
          </div>
        </section>
      </div>

      <DialogFooter className='tf-right-drawer-footer justify-end sm:space-x-0'>
        <div className='flex items-center gap-3'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className='h-10 rounded-xl border-[#e7e9ef] bg-white p-3 text-sm font-medium leading-none text-[#171a20]'
          >
            {t('assets.actions.cancel')}
          </Button>
          <Button
            type='button'
            onClick={submit}
            disabled={isSaving}
            className='h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] p-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'
          >
            {t('assets.actions.save')}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  )
}
